import Bugsnag from '@bugsnag/js';
import { AppProps } from 'next/app';
import React, { useEffect, useState } from 'react';
import { ReactQueryDevtools } from 'react-query/devtools';
import smoothscroll from 'smoothscroll-polyfill';
import { validate as uuidValidate } from 'uuid';
import useLocalStorage from 'hooks/useLocalStorageSafe';

import '../src/app/assets/scss/main.scss';

import '../src/app/assets/scss/_rc_slider.scss';
import '../src/app/assets/scss/_toggle_switch.scss';

import 'components/remodal/Remodal.scss';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import '../src/app/assets/scss/blw_progress_bar_mobile.scss';

import GlobalStyles from 'globalStyles';
import { useRouter } from 'next/router';
import { Hydrate, QueryClient, QueryClientProvider } from 'react-query';
import { ClerkProvider } from '@clerk/nextjs';
import 'nprogress/nprogress.css';
import { BreakpointProvider } from 'components/BreakpointProvider';
import { getDefaultAnalyticsTraits } from 'helpers/analytics';
import { queries } from 'helpers/breakpoints';
import { Provider as JotaiProvider } from 'jotai';
import Cookies from 'js-cookie';
import dynamic from 'next/dynamic';
import { trackEvent } from 'utils/tracking/trackEvent';
import useRudderStackTrack from 'hooks/useRudderStackTrack';
import useAnalyticsTrack from 'hooks/useAnalyticsTrack';
import boatsetterPaths from 'utils/boatsetterPaths';
import { StyleSheetManager } from 'styled-components';
import isPropValid from '@emotion/is-prop-valid';
import useAuthCleanup from 'hooks/useAuthCleanup';
import SessionActivator from 'auth/sessionActivator';
import RouteChangeIndicator from 'components/RouteChangeIndicator';
import init from '../src/BugSnagger';
import AppProvider from '../src/app/components/AppProvider';
import SEO from '../src/app/components/SEO';
import MetaInfo from '../src/app/components/pdp/v3/MetaInfo';

const SnackbarProvider = dynamic(() => import('components/banners/CustomSnackbarProvider'), {
  ssr: false,
});

const AuthCleanupBridge = ({ children }) => {
  useAuthCleanup();
  return children;
};

init();

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React);

const isPdp = (pathname: string): boolean => pathname === '/boats/[id]';

export const createRudderStackPageEvent = (appRouter, retryCount = 1, props = {}) => {
  const defaultAnalyticsTraits = getDefaultAnalyticsTraits();
  // We need to wait for the client id to be defined before we can send the page event
  // We also want to limit the number of retries to 5

  if (retryCount > 5 || defaultAnalyticsTraits.ga_client_id) {
    const pageProps = {
      ...getDefaultAnalyticsTraits(),
      url: window.location.href,
      ...props,
    };
    window.rudderanalytics?.page(appRouter.pathname, document.title, pageProps);
  } else {
    setTimeout(() => {
      // If the client id is not defined, we will retry in 500ms
      createRudderStackPageEvent(appRouter, retryCount + 1, props);
    }, 500);
  }
};

const MyApp = ({ Component, pageProps }: AppProps) => {
  const eventIntakeTrack = useAnalyticsTrack();

  useEffect(() => {
    smoothscroll.polyfill();
  }, []);

  const appRouter = useRouter();
  const [queryClient] = useState(() => new QueryClient());
  const rudderTrack = useRudderStackTrack();
  const [galacticaSession] = useLocalStorage('galactica_session', '');

  const onPageChange = () => {
    const { referrer } = document || {};

    if (appRouter.pathname !== boatsetterPaths.search) createRudderStackPageEvent(appRouter);
    trackEvent({
      event: 'customSitePageview',
      page: appRouter.pathname,
      referrer,
    });

    rudderTrack('customSitePageview', { page: appRouter.pathname, referrer });
    eventIntakeTrack('UserViewedPage', {
      anonymousId: Cookies.get('uuid') || '',
      sessionId: galacticaSession || '',
      appScreen: appRouter.pathname,
    });
  };

  // Wait for RudderStack SDK to fully load before reading anonymous ID.
  // v3 loads async — the stub's getAnonymousId() just queues, it doesn't return a value.
  const syncAnonymousIdCookies = () => {
    window.rudderanalytics?.ready(() => {
      const anonymousUUID = window.rudderanalytics?.getAnonymousId();
      if (uuidValidate(anonymousUUID)) {
        if (!Cookies.get('initial_anonymous_id')) {
          Cookies.set('initial_anonymous_id', anonymousUUID);
        }
        Cookies.set('anonymous_id', anonymousUUID);
      }
    });
  };

  // This implements the default behavior from styled-components v5
  const shouldForwardProp = (propName, target) => {
    if (typeof target === 'string') {
      // For HTML elements, forward the prop if it is a valid HTML attribute
      return isPropValid(propName);
    }
    // For other elements, forward all props
    return true;
  };

  useEffect(() => {
    document.documentElement.dataset.hydrated = '';
    syncAnonymousIdCookies();
    onPageChange();
  }, []);

  useEffect(() => {
    appRouter.events.on('routeChangeComplete', onPageChange);

    return () => {
      appRouter.events.off('routeChangeComplete', onPageChange);
    };
  }, [appRouter]);

  const isPdpPage = isPdp(appRouter.pathname);
  let boatId: string | null;
  if (isPdpPage) {
    boatId = appRouter.query.id as string | null;
  }

  return (
    <ErrorBoundary>
      <GlobalStyles />
      <StyleSheetManager shouldForwardProp={shouldForwardProp} enableVendorPrefixes>
        {/*
        ATTN: The opening bracket must be on the line above. The closing bracket must be on the line below.
        //@ts-expect-error: Legacy error */}
        <ClerkProvider {...pageProps}>
          <SessionActivator />
          <QueryClientProvider client={queryClient}>
            <AuthCleanupBridge>
              <Hydrate state={pageProps.dehydratedState}>
                {!isPdpPage && <SEO />}
                {isPdpPage && <MetaInfo boatId={boatId} />}
                <JotaiProvider>
                  <BreakpointProvider queries={queries}>
                    <div id="app">
                      <RouteChangeIndicator />
                      <SnackbarProvider />
                      <AppProvider>
                        <Component {...pageProps} />
                      </AppProvider>
                    </div>
                  </BreakpointProvider>
                </JotaiProvider>
              </Hydrate>
              {process.env.NODE_ENV === 'development' && <ReactQueryDevtools initialIsOpen={false} />}
            </AuthCleanupBridge>
          </QueryClientProvider>
        </ClerkProvider>
      </StyleSheetManager>
    </ErrorBoundary>
  );
};
export default MyApp;
